import React, { createContext, useContext, useMemo, useState } from 'react';
interface PricingState {
  adSpend: number;
  cpaLow: number;
  cpaHigh: number;
  hasRecruiter: boolean;
  hasLeader: boolean;
  splitBuildFee: boolean;
  setAdSpend: (next: number) => void;
  setCpaLow: (next: number) => void;
  setCpaHigh: (next: number) => void;
  setHasRecruiter: (next: boolean) => void;
  setHasLeader: (next: boolean) => void;
  setSplitBuildFee: (next: boolean) => void;
  // Derived
  buildFee: number;
  adSpendChunks: number;
  scopeMultiplier: number;
  platformFee: number;
  campaignFee: number;
  recruiterFee: number;
  monthlyTotal: number;
  hiresLow: number;
  hiresHigh: number;
  // Leadership add-on
  leaderBuildFee: number;
  leaderMonthlyFee: number;
  leaderActiveFee: number;
  leaderAdSpendLow: number;
  leaderAdSpendHigh: number;
}
const PricingContext = createContext<PricingState | null>(null);
export function PricingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [adSpend, setAdSpend] = useState(5000);
  const [cpaLow, setCpaLow] = useState(200);
  const [cpaHigh, setCpaHigh] = useState(500);
  const [hasRecruiter, setHasRecruiter] = useState(false);
  const [hasLeader, setHasLeader] = useState(false);
  const [splitBuildFee, setSplitBuildFee] = useState(false);
  const value = useMemo<PricingState>(() => {
    // Flat $4,500 at any campaign scope, +$1,500 when a recruiter is added.
    const buildFee = 4500 + (hasRecruiter ? 1500 : 0);
    // Ad spend drives scope: one chunk per $2,500 under management.
    const adSpendChunks = Math.max(1, Math.ceil(adSpend / 2500));
    // Scope is counted in whole steps: one per $2,500 of ad spend.
    const scopeMultiplier = adSpendChunks;
    const platformFee = 1500;
    const campaignFee = 1000 * adSpendChunks;
    const recruiterFee = 1500 * adSpendChunks;
    const monthlyTotal = platformFee + campaignFee + (hasRecruiter ? recruiterFee : 0);
    const hiresLow = Math.max(1, Math.round(adSpend / cpaHigh));
    const hiresHigh = Math.max(hiresLow + 1, Math.round(adSpend / cpaLow));
    return {
      adSpend,
      cpaLow,
      cpaHigh,
      hasRecruiter,
      hasLeader,
      splitBuildFee,
      setAdSpend,
      setCpaLow,
      setCpaHigh,
      setHasRecruiter,
      setHasLeader,
      setSplitBuildFee,
      buildFee,
      adSpendChunks,
      scopeMultiplier,
      platformFee,
      campaignFee,
      recruiterFee,
      monthlyTotal,
      hiresLow,
      hiresHigh,
      leaderBuildFee: 1000,
      // Leadership monthly service follows the standard campaign structure,
      // priced at the $4,000 of ad spend the leadership campaign runs on.
      leaderMonthlyFee: platformFee + 1000 * Math.ceil(4000 / 2500),
      leaderActiveFee: 2500,
      // A leadership campaign runs at $4,000 of managed ad spend.
      leaderAdSpendLow: 4000,
      leaderAdSpendHigh: 4000
    };
  }, [adSpend, cpaLow, cpaHigh, hasRecruiter, hasLeader, splitBuildFee]);
  return <PricingContext.Provider value={value} data-id="element-619" data-mp="3b7byesamhf4k">{children}</PricingContext.Provider>;
}
export function usePricing(): PricingState {
  const context = useContext(PricingContext);
  if (!context) {
    throw new Error('usePricing must be used within a PricingProvider');
  }
  return context;
}